import { partitionItemIdsByReadState } from "domains/concierge/internal/util/partitionItemIdsByReadState";
import { Conversation } from "domains/concierge/schemas/ConversationSchema";
import { UnreadItemsSummary } from "domains/concierge/schemas/UnreadItemsSummarySchema";
import { sortedIndex } from "es-toolkit/compat";

// TODO: This should be removed once es-toolkit/compat is updated to include this function.
// The implementation is merged https://github.com/toss/es-toolkit/pull/971 but not released yet.
/**
 * This method is like `indexOf` but performs a binary search on a sorted array.
 * @param {ArrayLike<T> | null | undefined} array The sorted array to inspect.
 * @param {T} value The value to search for.
 * @returns {number} Returns the index of the matched value, else -1.
 * @example
 *
 * sortedIndexOf([4,5,5,5,6], 5) => 1
 * sortedIndexOf([1.1, 2.2, 3.3], 2.2) => 1
 */
export function sortedIndexOf<T>(
    array: ArrayLike<T> | null | undefined,
    value: T,
): number {
    if (!array?.length) return -1;

    const index = sortedIndex(array, value);
    if (index < array.length && Object.is(array[index], value)) return index;
    return -1;
}

/**
 * Takes an unread summary and a conversation update and calculates a new
 * version of the update summary. It adds any new unread items and removes any
 * unread items that have been read. If it returns an unread items summary it
 * means the conversation is still unread, if it returns undefined it means the
 * conversation is no longer unread.
 */
export const createUnreadItemsSummary = (
    current: UnreadItemsSummary | undefined,
    conversation: Conversation,
    userId: string,
): UnreadItemsSummary => {
    // Check if the conversation update is stale
    const isUpdateStale =
        !!current && current.latestToken > conversation.latestToken;

    // If stale, prefer the details from the current summary. Otherwise take
    // details from the conversation update.
    const result = isUpdateStale
        ? current
        : {
              conversationId: conversation.id,
              patientId: conversation.regardingPatientId,
              assignee: conversation.assignee,
              status: conversation.status,
              system: conversation.source.system,
              latestToken: conversation.latestToken,
              itemIds: [...(current?.itemIds ?? [])],
          };

    const partitioned = partitionItemIdsByReadState(conversation, userId);

    // Remove any items that are now read
    for (const itemId of partitioned.read) {
        const index = sortedIndexOf(result.itemIds, itemId);
        if (index > -1) {
            result.itemIds.splice(index, 1);
        }
    }

    // Insert any new unread items
    for (const itemId of partitioned.unread) {
        const index = sortedIndex(result.itemIds, itemId);
        if (result.itemIds[index] !== itemId) {
            result.itemIds.splice(index, 0, itemId);
        }
    }

    return result;
};
